import { IAsynchronouslyLoadedValue } from './asynchronouslyLoadedValue';
import { ClaimMetadata } from './claimList';

export type ClaimState = {
    claim: IAsynchronouslyLoadedValue<Claim>;
};

export enum ClaimStatus {
    APPROVED = 'APPROVED',
    CREATED = 'CREATED',
    IN_PROGRESS = 'IN_PROGRESS',
    ERRORED = 'ERRORED',
    IMPORTED = 'IMPORTED',
}

export type Claim = {
    resellerClaimNumber: string;
    resellerId: string;
    fileId: string;
    submissionDate: number;
    errorList: string[];
    claimType: ClaimType;
    status: ClaimStatus;
    createdBy: string;
    version: number;
};

export interface ClaimAttachedDocument {
    id: string;
    type: string;
    name: string;
    description: string;
    uploadedAt: Date;
    uploadedBy?: string;
    presignedURL: string;
}

export enum ClaimType {
    TPR = 'TPR',
    ADHOC = 'Ad Hoc',
    MAP = 'MAP',
    MDF = 'MDF',
    HARD_DOLLARS = 'Hard Dollars',
    SHORTAGES_OTHER = 'Shortages - Other',
    SHORTAGES_CONCEALED = 'Shortages - Concealed',
    RETURNS_DISPUTES = 'Returns Disputes',
    FREIGHT_DISPUTES = 'Freight Disputes',
    RESELLER_COMPLIANCE = 'Reseller Compliance',
    DAMAGED_DEFECTIVE = 'Damaged/Defective',
    COST_DISPUTE = 'Cost Dispute',
    TERM_DISCOUNT = 'Term Discount',
    PRICE_PROTECTION = 'Price Protection',
    SALES_TAX_CREDIT = 'Sales Tax Credit',
    BAD_DEBT = 'Bad Debt',
    END_OF_LIFE = 'End Of Life',
    POST_AUDIT = 'Post Audit',
    FTVE = 'FTVE',
    OTHER = 'Other',
}

export enum ClaimCategory {
    PROMOTIONAL = 'Promotional',
    SCHEDULED_CONTRACTUAL = 'Scheduled/Contractual',
    ADHOC = 'Ad Hoc',
    PRICE_CONCESSION = 'Price Concession',
    WRITE_OFF = 'Write Off',
}

export const claimStatuses = ['CREATED', 'APPROVED', 'IN_PROGRESS', 'ERRORED'];

export enum ClaimReceiptMethods {
    EMAIL_NON_PARAGON = 'Email (Non-Paragon)',
    PARAGON = 'Paragon',
    PARAGON_TRANSFORMATION_REQUIRED = 'Paragon - Transformation Required',
    PARAGON_SSP = 'Paragon SSP',
    REMITTANCE = 'Remittance',
    WORKDOCS = 'WorkDocs',
    TICKET = 'Ticket',
    TICKET_TRANSFORMATION_REQUIRED = 'Ticket - Transformation Required',
    CUSTOMER_PORTAL = 'Customer Portal',
    EMAIL_RING = 'Email (Ring)',
    OTHER = 'Other',
    CLAIM_SCHEDULE = 'Claim Schedule',
    CLAIM_TEMPLATE_UPLOAD = 'Claim Template Upload',
}

export type ClaimUploadProgress = {
    fileName: string;
    progress: number;
};

export type ClaimProcessingStatus = typeof claimStatuses[number];

export type DetailedClaimState = {
    detailedClaim: IAsynchronouslyLoadedValue<ClaimWithMetadata>;
};

export type ClaimWithMetadata = {
    detailedClaim: DetailedClaim;
    claimMetadata: ClaimMetadata;
};

export type DetailedClaim = {
    claimId: string;
    resellerClaimNumber: string;
    status: DetailedClaimStatus;
    startDate: number;
    endDate: number;
    claimTotal: number;
    currency: string;
    receiptDate: number;
    requestor: string;
    claimCategory: string;
    claimType: string;
    description: string;
    creditMemoStatus: string;
    creditMemoNumber: string;
    claimLineItems: DetailedClaimLineItem[];
    promotionClaims: DetailedPromotionClaim[];
    supplementalLineItems: Record<string, DetailedSupplementalLineItem>;
    contractId?: string;
    contractExpenseDescription?: string;
};

export type DetailedClaimLineItem = {
    claimLineItemId: string;
    asin: string;
    itemId: string;
    family: string;
    program: string;
    promotionId: string;
    unitsClaimed: number;
    claimAmount: number;
    fundingAmount: number;
    overClaimedAmount: number;
    overClaimedUnits: number;
};

type DetailedPromotionClaim = {
    promotionId: string;
    description: string;
    promotionCountryCode: string;
    promotionType: string;
    startDate: number;
    endDate: number;
    promotionCurrency: string;
    discountLevelLocal: number;
    promotionStatus: string;
    stackable: boolean;
    asins: string[];
};

type DetailedSupplementalLineItem = {
    accrualAmount: number;
    accrualQuantity: number;
    asin: string | null;
    claimedUnits: number;
    fundingSplitValue: number;
    grossSales: number | null;
    grossSalesSources: string[];
    percentageOfSales: number;
    promotionId: string | null;
    returns: number;
    sales: number | null;
    salesSources: string[];
    shipments: number;
};

export enum DetailedClaimStatus {
    DRAFT = 'DRAFT',
    SUBMITTED = 'SUBMITTED',
    DISCARDED = 'DISCARDED',
    APPROVED = 'APPROVED',
    PARTIALLY_APPROVED = 'PARTIALLY_APPROVED',
    REJECTED = 'REJECTED',
    REWORK = 'REWORK',
}
